import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import DifferentInitialIncomePayment from "./DifferentInitialIncomePayment";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { FrequencyTypeArrays, processTypeEnum } from "../../Resource/Contents";
import { PensionTransfer } from "../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import {
    calculateGrossIncomePerPayment,
    calculateMaximumAvailablePerPayment,
    calculateMaximumIncomePerPaymentWithStandardAlgorithm,
    calculateMaximumIncomePerPaymentWithTargetAlgorithm,
    calculateTotalIncomePaymentFromPension,
    frequencyMap,
    getInitialPaymentRequested,
    shouldDisplayDifferentInitialIncomePayment,
} from "./Helper/IncomePaymentHelper";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { InvalidateDate } from "../../../../../../../react-core/Datepicker/Resources/ContentContent";
import { addYears, stripToDay } from "../../../../../../../react-core/utils/date";
import { nbSippIncomePaymentDetailsLabels } from "../../../NewBusinessWizard/Resource/Contents";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { calculateAnnualIncomeAmount } from "../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { DrawdownAnnualAmount } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualAmount";
import { IJsonDate } from "../../../../../../../react-core/Utils/JsonDateParser";
import { PensionLevelDrawdownIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/PensionLevelDrawdownIncome";
import { RegularCrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/RegularCrystallisationDetails";

interface IIncomeRequiredProps extends IProcessComponentProps {
    data: DrawdownAnnualIncome;
    onChange: (changes: DrawdownAnnualIncome) => void;
    labels: any;
    messages: any;
    drawdownType: DrawdownType;
    pensionTransfer?: PensionTransfer[];
    regularCrystalDifferentInital?: boolean;
    regularCrystallisationDetails?: RegularCrystallisationDetails;
    processType?: processTypeEnum;
    hasCappedArrangement?: boolean;
    pensionBase: PensionBase;
    isITFC: boolean;
}
interface IIncomeRequiredState {
    incomeConstraints: PensionLevelDrawdownIncome
}

export class IncomeRequired extends ProcessComponent<IIncomeRequiredProps, IIncomeRequiredState> {
    _isMounted = false;

    constructor(props: any) {
        super(props);
        this.state = { incomeConstraints: null}
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        const { data, onChange, drawdownType, regularCrystalDifferentInital } = this.props;
        super.componentDidMount();
        if (drawdownType === DrawdownType.Regular && regularCrystalDifferentInital) {
            onChange({ TakeDifferentInitialIncomePayment: true });
        }
        if (drawdownType !== DrawdownType.Regular && data?.TargetSpecificArrangements != true) {
            onChange({ TargetSpecificArrangements: false });
        }

        this.setState({
            incomeConstraints: {
                AnnualIncome: data
            }
        });
    }

    private calculateMaximumIncomePaymentByStandard = (
        processType: processTypeEnum,
        pensionBase: PensionBase,
        isITFC: boolean,
    ) => {

        const frequency = pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.Frequency ||
            Frequency.Monthly;

        const crystallisationAmount = pensionBase?.DrawdownModel?.CrystallisationDetails?.CrystallisedValue?.Amount || 0;

        const incomeConstraintFromWizard = calculateMaximumIncomePerPaymentWithStandardAlgorithm(processType, pensionBase, isITFC)+crystallisationAmount;

        const initialPaymentValue = getInitialPaymentRequested(pensionBase, processType) || 0;

        const returnValue = (incomeConstraintFromWizard - initialPaymentValue > 0 ? incomeConstraintFromWizard - initialPaymentValue:
                ((this.state?.incomeConstraints?.AnnualIncome?.MaximumIncomePerPayment?.Amount || 0) - initialPaymentValue))
            / frequencyMap[frequency];

        return returnValue < 0 ? 0 : returnValue;
    };

    private calculateMaximumIncomePaymentByTarget = (
        processType: processTypeEnum,
        pensionBase: PensionBase,
        isITFC: boolean,
    ) => {

        const crystallisationAmount = pensionBase?.DrawdownModel?.CrystallisationDetails?.CrystallisedValue?.Amount;

        const incomeConstraintFromWizard = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
            processType,
            pensionBase,
            isITFC,
        ) + crystallisationAmount;

        const initialPaymentValue = getInitialPaymentRequested(pensionBase, processType) || 0;

        const frequency = pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.Frequency ||
            Frequency.Monthly;

       

        const returnValue = (incomeConstraintFromWizard - initialPaymentValue > 0 ? incomeConstraintFromWizard - initialPaymentValue :
            ((this.state?.incomeConstraints?.AnnualIncome?.MaximumIncomePerPayment?.Amount || 0) - initialPaymentValue))
            / frequencyMap[frequency];

        return returnValue < 0 ? 0 : returnValue;
    };

    yearFromNow = () => {
        const result = stripToDay(addYears(new Date(), 1));
        if (result.getDate() > 28) {
            result.setDate(28);
        }
        return result;
    };

    private calculateAnnualAmount(grossIncomePerPaymentAmount : number, frequency : Frequency) : DrawdownAnnualAmount{
        if (grossIncomePerPaymentAmount != null && grossIncomePerPaymentAmount != undefined &&
            frequency != null && frequency != undefined) {
            return { Amount: calculateAnnualIncomeAmount(grossIncomePerPaymentAmount, frequency) }
        }
        return {Amount: { Amount: 0, Currency: CurrencyCode.GBP }};
    }

    private showMaximumIncomePerPaymentField(data: DrawdownAnnualIncome): boolean {
        return !data?.TargetSpecificArrangements ||
            (data?.TargetSpecificArrangements && this.props.processType === processTypeEnum.Quote);
    }

    public render(): JSX.Element {
        const {
            data,
            labels,
            onChange,
            messages,
            drawdownType,
            pensionTransfer,
            processType,
            hasCappedArrangement,
            regularCrystalDifferentInital,
            pensionBase,
            isITFC,
        } = this.props;
        
        let frequency = drawdownType === DrawdownType.Regular
            ? pensionBase?.DrawdownModel.RegularCrystallisationDetails?.Frequency
            : data?.Frequency;
        if (frequency === Frequency.NotSpecified) {
            frequency = FrequencyTypeArrays[0];
        }

        if (drawdownType !== DrawdownType.Regular && data?.TargetSpecificArrangements == null) {
           data.TargetSpecificArrangements = false;
        }

        return (
            <>
                {drawdownType !== DrawdownType.Regular && (
                    <div>                  
                    <RadiosEditor
                        cssWrapper="rg-inline mb-16"
                        label={labels.targetSpecificArrangements}
                        selected={data?.TargetSpecificArrangements || false}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        tooltip={labels.targetSpecificArrangementsTooltip}
                        onChange={(v) => {
                            if (v) {
                                onChange({
                                    TargetSpecificArrangements: v,
                                    TakeDifferentInitialIncomePayment: false,
                                    GrossInitialIncomePayment: {
                                        Amount: data?.GrossInitialIncomePayment?.Amount ?? 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPayment: {
                                        Amount: data?.GrossIncomePerPayment?.Amount ?? 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPaymentFromPension: {
                                        Amount: data?.GrossIncomePerPaymentFromPension?.Amount ?? 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    Frequency: frequency,
                                    AnnualAmount: this.calculateAnnualAmount(data?.GrossIncomePerPayment?.Amount ?? 0, frequency),
                                    FlexiIncomeReviewDate:
                                        data?.FlexiIncomeReviewDate &&
                                        data?.FlexiIncomeReviewDate.toString() != InvalidateDate
                                            ? data?.FlexiIncomeReviewDate
                                            : this.yearFromNow() as IJsonDate,
                                    FrequencyFlexiIncomeReview:
                                        data?.FrequencyFlexiIncomeReview && data?.FrequencyFlexiIncomeReview != 0
                                            ? data?.FrequencyFlexiIncomeReview
                                            : 12,
                                });
                            } else {
                                onChange({
                                    TargetSpecificArrangements: v,
                                    GrossInitialIncomePayment: {
                                        Amount: data?.GrossInitialIncomePayment?.Amount ?? 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPayment: {
                                        Amount: data?.GrossIncomePerPayment?.Amount ?? 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    Frequency: frequency,
                                    AnnualAmount: this.calculateAnnualAmount(data?.GrossIncomePerPayment?.Amount ?? 0, frequency),
                                    FlexiIncomeReviewDate: null,
                                    FrequencyFlexiIncomeReview: null,
                                });
                            }
                        }}
                    />
                    </div>
                )}
                {!data?.TargetSpecificArrangements && (drawdownType === DrawdownType.Capped || (pensionTransfer && pensionTransfer.find((transfer) => transfer.DrawdownType === DrawdownType.Capped && !transfer.PensionDetails.IsDependentsPension)) ) && (
                    <Alert
                        message={messages.standardAlgorithmCappedMessage}
                        alertType={AlertType.Warning}
                        data-test="standardAlgorithmCappedMessage"
                    />
                )}
                {(!data?.TargetSpecificArrangements && pensionTransfer &&pensionTransfer.find((transfer) => transfer.PensionDetails.IsDependentsPension)) && (
                    <Alert
                        message={messages.inheritedFundMessage}
                        alertType={AlertType.Warning}
                        data-test="inheritedFundMessage"
                    />
                )}
                {shouldDisplayDifferentInitialIncomePayment(
                    data,
                    drawdownType,
                    pensionTransfer,
                    hasCappedArrangement,
                    processType,
                    regularCrystalDifferentInital
                ) && (
                    <DifferentInitialIncomePayment
                        data={data}
                        onChange={(v) => {
                            onChange(v);
                        }}
                        labels={labels}
                        messages={messages}
                        drawdownType={drawdownType}
                        onRef={(ref) => this.appendChild(ref)}
                        pensionBase={pensionBase}
                        processType={processType}
                        isITFC={isITFC}
                        regularCrystalDifferentInital={regularCrystalDifferentInital}
                    />
                )}
                {drawdownType === DrawdownType.Regular ? (
                    <div className="row mb-24">
                        <span className="col-auto">{labels.maximumAvailablePerPayment}</span>
                        <span className="col-fluid" data-test="maximumAvailablePerPayment">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                calculateMaximumAvailablePerPayment(processType, pensionBase?.DrawdownModel),
                                CurrencyCode.GBP
                            )}
                        </span>
                    </div>
                ) : this.showMaximumIncomePerPaymentField(data) && (
                    <div className="row mb-24">
                        <span className="col-auto">{labels.maximumIncomePerPayment}</span>
                        <span className="col-fluid" data-test="maximumIncomePerPayment">
                            {data?.TargetSpecificArrangements !== true
                                ? MoneyDisplayHelper.FromatMoneyDisplay(
                                    calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                                        processType,
                                        pensionBase,
                                        isITFC,
                                    ),
                                    CurrencyCode.GBP
                                )
                                : MoneyDisplayHelper.FromatMoneyDisplay(
                                    calculateMaximumIncomePerPaymentWithTargetAlgorithm(
                                        processType,
                                        pensionBase,
                                        isITFC,
                                    ),
                                    CurrencyCode.GBP
                                )}
                        </span>
                    </div>
                )}
                {!data?.TargetSpecificArrangements && (
                    <div className="row">
                        <div className="col-4 ">
                            <MoneyTextBoxEditor
                                label={labels.grossIncomePerPayment}
                                value={ data?.AnnualAmount?.Amount?.Amount > 0 ? calculateGrossIncomePerPayment(data)?.Amount : data?.GrossIncomePerPayment?.Amount }
                                currency={data?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP}
                                onChange={(v) => {
                                    onChange({
                                        GrossIncomePerPayment: {
                                            Amount: v,
                                            Currency: data?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP,
                                        },
                                        Frequency: frequency,
                                        AnnualAmount: this.calculateAnnualAmount(v, frequency)
                                    });
                                }}
                                required={messages.grossIncomePerPaymentRequired}
                                validationRules={[
                                    {
                                        errorMessage: "Please enter an amount that is greater than zero.",
                                        validator: (v) => v > 0,
                                    },
                                ]}
                                atMost={{
                                    errorMessage:
                                        drawdownType === DrawdownType.Regular
                                            ? messages.grossAvailablePerPaymentOver
                                            : messages.grossIncomePerPaymentOver,
                                    value:
                                        drawdownType === DrawdownType.Regular
                                            ? Number(calculateMaximumAvailablePerPayment(
                                            processType,
                                            pensionBase?.DrawdownModel
                                        ).toFixed(2)) || 0
                                            : Number(calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                                                processType,
                                                pensionBase,
                                                isITFC,
                                            ).toFixed(2)),
                                }}
                            />
                        </div>
                        <CheckboxEditor
                            label={nbSippIncomePaymentDetailsLabels.takeFullValue}
                            value={data?.GrossIncomePerPayment?.Amount==(drawdownType === DrawdownType.Regular
                                ? calculateMaximumAvailablePerPayment(
                                processType,
                                pensionBase?.DrawdownModel
                            ) || 0
                                : calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                                    processType,
                                    pensionBase,
                                    isITFC,
                                ))}
                            onChange={(v) => {
                                var amount = v ? drawdownType === DrawdownType.Regular
                                    ? calculateMaximumAvailablePerPayment(
                                    processType,
                                    pensionBase?.DrawdownModel
                                ) || 0
                                    : calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                                        processType,
                                        pensionBase,
                                        isITFC,
                                    ):0
                                onChange({
                                    GrossIncomePerPayment: {
                                        Amount: Number(amount.toFixed(2)),
                                        Currency: data?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP,
                                    },
                                    TakeFullValue: v,
                                    Frequency: frequency,
                                    AnnualAmount: this.calculateAnnualAmount(amount, frequency)
                                });
                            }}
                        />
                    </div>
                )}
                {data?.TargetSpecificArrangements && processType === processTypeEnum.Quote && (
                    <div className="row">
                        <div className="col-4 ">
                            <MoneyTextBoxEditor
                                label={labels.grossIncomePerPaymentFromPension}
                                value={data?.GrossIncomePerPaymentFromPension?.Amount > 0 ? data.GrossIncomePerPaymentFromPension.Amount : calculateTotalIncomePaymentFromPension(pensionBase)?.Amount}
                                currency={data?.GrossIncomePerPaymentFromPension?.Currency || CurrencyCode.GBP}
                                validationRules={[
                                    {
                                        errorMessage: "Please enter an amount that is greater than zero.",
                                        validator: (v) => (v > 0),
                                    }
                                ]}
                                onChange={(v) => {
                                    const income = {
                                        Amount: v,
                                        Currency:
                                            data?.GrossIncomePerPaymentFromPension?.Currency || CurrencyCode.GBP,
                                    }
                                    onChange(
                                        {
                                            GrossIncomePerPaymentFromPension: income
                                        });
                                }}
                                required={messages.grossIncomePerPaymentFromPensionRequired}
                                atMost={{
                                    errorMessage: messages.grossIncomePerPaymentOver,
                                    value: Number(calculateMaximumIncomePerPaymentWithTargetAlgorithm(
                                        processType,
                                        pensionBase,
                                        isITFC,
                                    ).toFixed(2)),
                                }}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }
}

export default IncomeRequired;
